import { createBrowserRouter, Navigate } from 'react-router-dom';
import { AdminProtectedRoute } from '@/features/auth/components/AdminProtectedRoute';
import { PlayerProtectedRoute } from '@/features/auth/components/PlayerProtectedRoute';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { PlayerLayout } from '@/components/layout/PlayerLayout';
import { LandingPage } from '@/pages/LandingPage';
import { PlayerLoginPage } from '@/pages/auth/PlayerLoginPage';
import { PlayerRegisterPage } from '@/pages/auth/PlayerRegisterPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { ContactPage } from '@/pages/ContactPage';
import { AdminLoginPage } from '@/pages/admin/AdminLoginPage';
import { AdminDashboard } from '@/pages/admin/AdminDashboard';
import { CreateCategoryPage } from '@/pages/admin/CreateCategoryPage';
import { EditCategoryPage } from '@/pages/admin/EditCategoryPage';
import { QuestionListPage } from '@/pages/admin/QuestionListPage';
import { CreateQuestionPage } from '@/pages/admin/CreateQuestionPage';
import { EditQuestionPage } from '@/pages/admin/EditQuestionPage';
import { UserListPage } from '@/pages/admin/UserListPage';
import { GameListPage } from '@/pages/admin/GameListPage';
import { ReportsPage } from '@/pages/admin/ReportsPage';
import { LifelinesPage } from '@/pages/admin/LifelinesPage';
import { ImportQuestionsPage } from '@/pages/admin/ImportQuestionsPage';
import { Lobby } from '@/features/game/components/Lobby';
import { GameSession } from '@/features/game/components/GameSession';
import { CategoryList } from '@/features/admin/components/Categories/CategoryList';
import { SectionList } from '@/features/admin/components/Sections/SectionList';
import { CreateSectionPage } from '@/pages/admin/CreateSectionPage';
import { EditSectionPage } from '@/pages/admin/EditSectionPage';
import { PlayerHomePage } from '@/pages/player/PlayerHomePage';
import { TeamSetupPage } from '@/pages/player/TeamSetupPage';
import { GameSettingsPage } from '@/pages/player/GameSettingsPage';
import { GameBoardPage } from '@/pages/player/GameBoardPage';
import { QuestionPage } from '@/pages/player/QuestionPage';
import { ResultsPage } from '@/pages/player/ResultsPage';
import { ProfilePage } from '@/pages/player/ProfilePage';


export const router = createBrowserRouter([
  // Public Landing Page (NOT protected)
  {
    path: '/',
    element: <LandingPage />,
  },
  // Player Login (Public)
  {
    path: '/login',
    element: <PlayerLoginPage />,
  },
  // Player Register (Public)
  {
    path: '/register',
    element: <PlayerRegisterPage />,
  },
  // Forgot Password (Public)
  {
    path: '/forgot-password',
    element: <ForgotPasswordPage />,
  },
  // Contact Page (Public)
  {
    path: '/contact',
    element: <ContactPage />,
  },
  // Admin Login (Public, separate from player login)
  {
    path: '/admin/login',
    element: <AdminLoginPage />,
  },
  // Player Routes (Protected - requires authentication)
  {
    path: '/player',
    element: (
      <PlayerProtectedRoute>
        <PlayerLayout />
      </PlayerProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <PlayerHomePage />,
      },
      {
        path: 'profile',
        element: <ProfilePage />,
      },
      {
        path: 'game-settings',
        element: <GameSettingsPage />,
      },
      {
        path: 'team-setup',
        element: <TeamSetupPage />,
      },
      {
        path: 'game',
        element: <GameBoardPage />,
      },
      {
        path: 'question',
        element: <QuestionPage />,
      },
      {
        path: 'results',
        element: <ResultsPage />,
      },
      {
        path: 'lobby',
        element: <Lobby />,
      },
      {
        path: 'game/:roomCode',
        element: <GameSession />,
      },
    ],
  },
  // Admin Routes (Protected - requires Admin role)
  {
    path: '/admin',
    element: (
      <AdminProtectedRoute>
        <AdminLayout />
      </AdminProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <AdminDashboard />,
      },
      // Sections Management
      {
        path: 'sections',
        element: <SectionList />,
      },
      {
        path: 'sections/create',
        element: <CreateSectionPage />,
      },
      {
        path: 'sections/edit/:id',
        element: <EditSectionPage />,
      },
      // Categories Management
      {
        path: 'categories',
        element: <CategoryList />,
      },
      {
        path: 'categories/create',
        element: <CreateCategoryPage />,
      },
      {
        path: 'categories/edit/:id',
        element: <EditCategoryPage />,
      },
      // Questions Management
      {
        path: 'questions',
        element: <QuestionListPage />,
      },
      {
        path: 'questions/create',
        element: <CreateQuestionPage />,
      },
      {
        path: 'questions/edit/:id',
        element: <EditQuestionPage />,
      },
      {
        path: 'questions/import',
        element: <ImportQuestionsPage />,
      },
      // Users Management
      {
        path: 'users',
        element: <UserListPage />,
      },
      // Games Management
      {
        path: 'games',
        element: <GameListPage />,
      },
      // Lifelines Management
      {
        path: 'lifelines',
        element: <LifelinesPage />,
      },
      // Reports
      {
        path: 'reports',
        element: <ReportsPage />,
      },
    ],
  },
  // Catch-all redirect
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]);
